import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  monthName as monthNameOf,
  createFormatter,
  invalidation,
  type ChangeItem,
  type ChangeValue,
  type Money,
} from '@sl/core';
import { useApiClient } from '../api/ApiClientProvider';
import { messageText } from '../messages';
import { useMessages, useShell } from '../shell/shellContext';
import { testId } from '../testId';
import { Button, Icon, Popover } from '../../ui';
import { BUDGET_EDIT_COPY, STAGED_COPY, TRAY_COPY } from './copy';
import { useStaged, useStagingAnnouncer } from './stagedContext';
import { useStagedNet } from './stagedNet';
import { LazyReviewDialog, useReviewDialogPrefetch } from '../../routes/review/lazy';
import styles from './StagedTray.module.css';

// THE STAGED-CHANGES TRAY — T-132; REQ-101 AC2 and AC6, `w07-review-publish.html` step 2.
//
// **The pill finally opens something.** Every edit an owner makes collects here, and REQ-101 AC2 says what a
// row is: **"Entity — what changes — from → to"**, with a remove per change and "Discard all". That sentence
// is `TRAY_COPY.summary` and it is one element, because it is one sentence.
//
// **IT OPENS WITHOUT ASKING THE SERVER ANYTHING.** The open change-set is already read once at the shell
// (T-129's `StagedProvider`), so the tray renders from `useStaged()` — no query, no skeleton, no round trip.
// `stagedTray.test.tsx` asserts that by counting `getChangeSet` calls across the open. That is what makes
// the Accept line's "opens ≤ 300 ms" a property of the design rather than a measurement that might drift.
//
// **WHY A POPOVER AND NOT A MODAL.** The trigger is the shell's own pill, in the top bar; an anchored panel
// hangs under it and covers part of the page and never the bar — so the mode chip stays visible beside it
// and D-165's exemption applies, exactly as it does for the budget editor and the switch confirm (the three
// staged surfaces are then one shape). A covering modal would owe its own chip and would take the account's
// name and the date range off the screen while you decide what to publish.
//
// **THE DISCARD CONFIRM IS A SECOND VIEW OF THIS PANEL, not a second layer.** A modal over a popover is two
// escape layers and two outside-click handlers fighting over one pointerdown: the modal's own scrim is
// "outside" the popover, so the tray would close under the confirm. One panel, two views, one layer — and
// the safe button keeps the focus.
//
// **WHAT IS NOT HERE.** No net total: ENGINE §11 computes `deltaAllowedPerDay` off the overlaid owners, not
// by adding three rows up, and asking `previewChanges` for it would cost the round trip the paragraph above
// just promised not to make. The total belongs on the review screen (REQ-104 AC2), which is where the
// mockup's docked bar was pointing all along.

// ---- the open state, shared by the pill and the reminder ---------------------------------------------------

interface TrayControl {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The pill: what the panel hangs off, and where the focus goes back to. */
  anchorRef: RefObject<HTMLElement | null>;
  /** T-133's review dialog, raised from this panel's own primary button. */
  reviewOpen: boolean;
  setReviewOpen: (open: boolean) => void;
}

const TrayContext = createContext<TrayControl | null>(null);

/** Mounted once, inside `<AppShell>`, so the pill, the reminder and the panel share one open state. */
export function TrayProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  // T-321: the review dialog is its own chunk now (); ask for it on idle, from the
  // one place that knows a tray exists on this screen, so a press never waits for a round trip.
  useReviewDialogPrefetch();
  // **Both close when the screen under them changes.** Found by driving the built app: the review dialog
  // covers the shell, and a hash change leaves it sitting over a screen it was never opened from — a scrim
  // the reader cannot explain and (in a real browser) cannot click past. The tray goes with it for the same
  // reason at 360, where it is nearly full width.
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
    setReviewOpen(false);
  }, [pathname]);
  const value = useMemo<TrayControl>(
    () => ({ open, setOpen, anchorRef, reviewOpen, setReviewOpen }),
    [open, reviewOpen],
  );
  return <TrayContext.Provider value={value}>{children}</TrayContext.Provider>;
}

const CLOSED: TrayControl = {
  open: false,
  setOpen: () => {},
  anchorRef: { current: null },
  reviewOpen: false,
  setReviewOpen: () => {},
};

/** Safe above the provider: a screen rendered outside the shell gets a tray that is simply never open. */
export function useTray(): TrayControl {
  return useContext(TrayContext) ?? CLOSED;
}

// ---- the panel ---------------------------------------------------------------------------------------------

export function StagedTray() {
  const { open, setOpen, anchorRef, reviewOpen, setReviewOpen } = useTray();
  const { items, count } = useStaged();
  const client = useApiClient();
  const queryClient = useQueryClient();
  const messages = useMessages();
  const { account, locale, deviceTimeZone } = useShell();
  const announce = useStagingAnnouncer();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // BUG-098: the set's net effect on daily spend for the foot, and the pencil's way to a row's editor.
  const net = useStagedNet();
  const navigate = useNavigate();
  const { requestEdit } = useStaged();
  const fmt = useMemo(
    () =>
      createFormatter({
        account: { currency: account.currency, timeZone: account.timezone },
        locale,
        deviceTimeZone,
      }),
    [account.currency, account.timezone, locale, deviceTimeZone],
  );
  // "September" — the month the rest-of-month figure runs to, in the reader's language, off the account's
  // own today (D-020's injected clock through the shell), never `new Date()`.
  const monthName = useMemo(() => monthNameOf(account.today, locale), [locale, account.today]);

  const close = useCallback(() => {
    setConfirming(false);
    setOpen(false);
  }, [setOpen]);

  const removeItem = useCallback(
    async (item: ChangeItem, summary: string) => {
      setBusy(item.id);
      try {
        await client.removeChange(account.id, item.id);
        await queryClient.invalidateQueries(invalidation.removeChange(account.id));
        // The server has answered, so the count is the truth rather than a guess.
        announce('removed', summary, Math.max(0, count - 1));
      } finally {
        setBusy(null);
      }
    },
    [account.id, announce, client, count, queryClient],
  );

  const discardAll = useCallback(async () => {
    setBusy('all');
    try {
      await client.discardChanges(account.id);
      await queryClient.invalidateQueries(invalidation.discardChanges(account.id));
      announce('removed', TRAY_COPY.title.toLowerCase(), 0);
      setConfirming(false);
    } finally {
      setBusy(null);
    }
  }, [account.id, announce, client, queryClient]);

  const one = count === 1;
  const n = String(count);

  // The review dialog outlives the panel: pressing "Review n changes" closes the tray and opens it, so it
  // is rendered here rather than inside the popover that raised it.
  if (!open) {
    return reviewOpen ? (
      <LazyReviewDialog open onClose={() => setReviewOpen(false)} returnFocusRef={anchorRef} />
    ) : null;
  }

  return (
    <Popover
      open
      onClose={close}
      anchorRef={anchorRef}
      placement="bottom-start"
      aria-label={TRAY_COPY.label(n, one)}
      width={440}
      className={styles.panel}
      testID={testId('tray', 'panel')}
      // The focus lands on the TITLE, not on the first control: the first control in this panel is
      // "Discard all", and a panel that opens with a destructive button under Enter is a trap. A screen
      // reader hears the dialog's name and the count, and Tab goes on from there (K6).
      initialFocusRef={titleRef}
      returnFocusRef={anchorRef}
      footer={
        confirming ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              testID={testId('tray', 'discard', 'confirm', 'cancel')}
              onClick={() => setConfirming(false)}
            >
              {TRAY_COPY.discardCancel}
            </Button>
            <Button
              variant="danger"
              size="sm"
              type="button"
              loading={busy === 'all'}
              testID={testId('tray', 'discard', 'confirm', 'accept')}
              onClick={() => void discardAll()}
            >
              {busy === 'all' ? TRAY_COPY.discardWorking : TRAY_COPY.discardConfirm}
            </Button>
          </>
        ) : count === 0 ? null : (
          <span className={styles.foot}>
            <span className={styles.kept}>
              {/* BUG-098 (w07 ①): "about −$17 per day overall" — ENGINE §11's figure from `previewChanges`, the
                  review's own number, said once here. Nothing when the set nets to nothing. */}
              {net !== null && net.amount !== 0 ? (
                <span data-testid={testId('tray', 'net')}>{TRAY_COPY.net(net.text)} · </span>
              ) : null}
              {TRAY_COPY.kept}
            </span>
            {/* REQ-101 AC6 — "Review n changes" in every mode, never "Publish". **T-133 built the screen
                it opens**: a modal over whatever is behind this panel (`w07` step 3), raised from here and
                from nowhere else. It was `aria-disabled` with the reason for exactly one lane. */}
            <Button
              variant="primary"
              size="sm"
              type="button"
              testID={testId('tray', 'review')}
              onClick={() => {
                setOpen(false);
                setReviewOpen(true);
              }}
            >
              {TRAY_COPY.review(n, one)}
            </Button>
          </span>
        )
      }
    >
      {confirming ? (
        <div className={styles.confirm} data-testid={testId('tray', 'discard', 'confirm')}>
          <h2 className={styles.confirmTitle}>{TRAY_COPY.discardTitle(n, one)}</h2>
          <p className={styles.confirmBody}>{TRAY_COPY.discardBody}</p>
        </div>
      ) : (
        <>
          <div className={styles.head}>
            <h2
              className={styles.title}
              tabIndex={-1}
              ref={titleRef}
              data-testid={testId('tray', 'title')}
            >
              {TRAY_COPY.title}
              {count > 0 ? (
                <>
                  <span className={styles.sep}>{' · '}</span>
                  <span className={styles.count} data-testid={testId('tray', 'count')}>
                    {n}
                  </span>
                </>
              ) : null}
            </h2>
            {count > 0 ? (
              <Button
                variant="danger-ghost"
                size="sm"
                icon="trash"
                type="button"
                testID={testId('tray', 'discard')}
                onClick={() => setConfirming(true)}
              >
                {TRAY_COPY.discardAll}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              icon="x"
              type="button"
              aria-label={TRAY_COPY.close}
              testID={testId('tray', 'close')}
              onClick={close}
            />
          </div>
          {count === 0 ? (
            <div className={styles.empty} data-testid={testId('tray', 'empty')}>
              <p className={styles.emptyTitle}>{TRAY_COPY.emptyTitle}</p>
              <p className={styles.emptyBody}>{TRAY_COPY.emptyBody}</p>
            </div>
          ) : (
            <ul className={styles.list} data-testid={testId('tray', 'list')}>
              {items.map((item, index) => (
                <TrayRow
                  key={item.id}
                  item={item}
                  index={index}
                  fmt={fmt}
                  month={monthName}
                  messages={messages}
                  busy={busy === item.id}
                  onRemove={removeItem}
                  onEdit={(it) => {
                    // The row's editor is on the tree (`ListRow`), which takes the ask by entity id once the
                    // level that lists the row is on screen; the tray closes, the way the review opens.
                    requestEdit(it.entity.id);
                    close();
                    navigate(`/campaigns?level=${it.entity.level}`);
                  }}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </Popover>
  );
}

// ---- one row -------------------------------------------------------------------------------------------

interface TrayRowProps {
  item: ChangeItem;
  /**
   * The row's id is `tray.item.<n>` and `<n>` is its place in the OPEN SET, oldest first — which is the
   * order the store keeps and the order DEMO_STORY's DS-38 binds (`tray.item.0` is the ad set that was
   * staged first). The item's own id rides as `data-item-id`, because `chi_1` is not a legal id segment
   * under D-019 (a lower-case kebab segment has no underscore) and a bound index is what the story asserts.
   */
  index: number;
  fmt: ReturnType<typeof createFormatter>;
  month: string;
  messages: Parameters<typeof messageText>[1];
  busy: boolean;
  onRemove: (item: ChangeItem, summary: string) => Promise<void>;
  /** BUG-098 (w07 ②): the pencil — reopen this budget's editor on the tree row that owns it. */
  onEdit: (item: ChangeItem) => void;
}

function TrayRow({ item, index, fmt, month, messages, busy, onRemove, onEdit }: TrayRowProps) {
  const root = testId('tray', 'item', String(index));
  const id = (part: string) => testId(root, part);
  const field =
    item.field === 'status'
      ? TRAY_COPY.fieldStatus
      : item.field === 'lifetime_budget'
        ? TRAY_COPY.fieldTotal
        : TRAY_COPY.fieldDaily;
  const from = valueText(item.from, item.field, fmt);
  const to = valueText(item.to, item.field, fmt);
  const summary = TRAY_COPY.summary(item.entity.name, field, from, to);
  const money = impactText(item, fmt, month, messages);
  // The engine's own sentence, and only where it says something the money line does not: a status change
  // names what STOPS or STARTS ("Turns off 3 ads"). For a budget it reads "$25 → $30 per day · about $5 per
  // day", which is the row's own first line and its second said again — the same duplication T-130 found in
  // the editor's impact line. A redistribution says it inside the money line instead, because there the
  // money line has nothing else to say.
  const sentence =
    item.field === 'status' && !isZeroImpact(item) ? messageText(item.consequence, messages) : null;

  return (
    <li className={styles.row} data-testid={root} data-item-id={item.id} data-field={item.field}>
      <span className={styles.icon} aria-hidden="true">
        <Icon name={iconFor(item)} size={14} />
      </span>
      <div className={styles.rowBody}>
        {/* REQ-101 AC2's sentence, whole and in one element — "Entity — what changes — from → to". The
            spans inside carry their own ids so QA can read a part without parsing the sentence. */}
        <p className={styles.summary} data-testid={id('summary')}>
          <span className={styles.entity} data-testid={id('entity')}>
            {item.entity.name}
          </span>
          <span className={styles.dash}>{' — '}</span>
          <span className={styles.field} data-testid={id('field')}>
            {field}
          </span>
          <span className={styles.sep}>{' · '}</span>
          <span className={styles.from} data-testid={id('from')}>
            {from}
          </span>
          {/* The arrow is READ, not hidden: `aria-hidden` here would make the pair "On Off" to a screen
              reader, and a visually-hidden " to " beside it would put "→  to" inside the element DS-38
              reads, where the story expects exactly "$25 → $30". One character, said once. */}
          <span className={styles.arrow}>{' → '}</span>
          <span className={styles.to} data-testid={id('to')}>
            {to}
          </span>
          {item.field === 'status' ? null : (
            <span className={styles.unit}>{` ${TRAY_COPY.perDay}`}</span>
          )}
        </p>
        <p className={styles.impact} data-testid={id('impact')}>
          {money}
        </p>
        {sentence ? (
          <p className={styles.sentence} data-testid={id('sentence')}>
            {sentence}
          </p>
        ) : null}
        {/* Staged with a problem: ENGINE §11's `below_minimum` / `below_spent` / `not_positive` are STAGED
            and come back as the item's own errors (the tech-lead's T-125 note (7)) — the tray is where they
            are fixed, and `previewChanges` refuses to publish while one is here. */}
        {item.errors.map((problem) => (
          <p key={problem.code} className={styles.error} data-testid={id('error')} role="alert">
            {messageText(problem.message, messages)}
          </p>
        ))}
        {item.warnings.map((warning) => (
          <p
            key={warning.code}
            className={styles.warning}
            data-testid={testId(root, 'warning', warning.code.replace(/_/g, '-'))}
            data-warning={warning.code}
          >
            {messageText(warning.message, messages)}
          </p>
        ))}
        {/* REQ-105 AC3 — the live value moved under a staged one. Said in words, because "$25 → $30" beside
            "it is $40 now" is three numbers and no sentence. */}
        {item.conflict ? (
          <p className={styles.conflict} data-testid={id('conflict')}>
            {TRAY_COPY.conflict(
              valueText(item.conflict.stagedFrom, item.field, fmt),
              valueText(item.conflict.current, item.field, fmt),
            )}
          </p>
        ) : null}
      </div>
      {item.field === 'status' ? null : (
        // BUG-098 (w07 ②, REQ-101 AC3): a pencil beside the × on a budget row — re-editing a staged budget no
        // longer means finding the row in the tree by hand.
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon="pencil"
          type="button"
          aria-label={TRAY_COPY.edit(summary)}
          testID={id('edit')}
          onClick={() => onEdit(item)}
        />
      )}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        icon="x"
        type="button"
        loading={busy}
        aria-label={TRAY_COPY.remove(summary)}
        testID={id('remove')}
        onClick={() => void onRemove(item, summary)}
      />
    </li>
  );
}

// ---- the words for a value, a row's icon and a row's money ------------------------------------------------

/** "On" · "Off" · "$25" — the same formatting the staged marker uses, so a pair reads as one kind of thing. */
export function valueText(
  value: ChangeValue,
  field: ChangeItem['field'],
  fmt: ReturnType<typeof createFormatter>,
): string {
  if (value.kind === 'status') return value.isOn ? STAGED_COPY.statusOn : STAGED_COPY.statusOff;
  return fmt(value.amount, field === 'daily_budget' ? 'money.perDay' : 'money.headline');
}

function iconFor(item: ChangeItem): 'play' | 'pause' | 'dollar' {
  if (item.field !== 'status') return 'dollar';
  return item.to.kind === 'status' && item.to.isOn ? 'play' : 'pause';
}

function isZeroImpact(item: ChangeItem): boolean {
  const { deltaPerDay, deltaRestOfMonth, delta30d } = item.impact;
  return [deltaPerDay, deltaRestOfMonth, delta30d].every((v) => v === null || v === 0);
}

/**
 * The money on a row: "about −$22 per day · −$200 for the rest of September".
 *
 * **When nothing moves it says so and then hands over to the engine**, because "no spend change" on its own
 * leaves the obvious question unanswered — an ad set's $8 a day does not evaporate, it goes to the sibling
 * ad, and the only sentence that says where is the server's (`change.turn_off_shifts`). That is the one
 * place this row shows a consequence for a change whose impact is zero.
 *
 * The 30-day window is deliberately not here: two figures is what a row of this width can hold, and the
 * third is on the review screen beside the forecast it belongs to (REQ-104 AC2).
 */
export function impactText(
  item: ChangeItem,
  fmt: ReturnType<typeof createFormatter>,
  month: string,
  messages: Parameters<typeof messageText>[1],
): string {
  if (isZeroImpact(item)) {
    return `${TRAY_COPY.noSpendChange} · ${messageText(item.consequence, messages)}`;
  }
  const parts: string[] = [];
  const { deltaPerDay, deltaRestOfMonth } = item.impact;
  if (deltaPerDay !== null && deltaPerDay !== 0)
    parts.push(TRAY_COPY.impactPerDay(signed(deltaPerDay, fmt)));
  if (deltaRestOfMonth !== null && deltaRestOfMonth !== 0)
    parts.push(BUDGET_EDIT_COPY.impactRestOfMonth(signed(deltaRestOfMonth, fmt), month));
  return parts.join(' · ');
}

/**
 * Money with its direction on the front — "+$5" / "−$200". The figure itself is the server's.
 *
 * **BUG-083 / D-195 — every projected effect of a change is WHOLE UNITS, on every surface.** This helper
 * used to take a precision argument and render the rest-of-month window at `money.exact`, on the reasoning
 * that the figure is a real remainder: Clearance – Lookalike 3% leaves exactly $199.80 of its total budget
 * unspent, and rounding it looked like inventing twenty cents. The product-manager ruled the other way and
 * the ruling is better, for two reasons this file had missed. **One:** REQUIREMENTS §7 row 5 already put
 * the projected effect of a change at `money.headline`, so the tray was the only surface disagreeing — the
 * same window read "−$199.80" here and "−$200" in T-137's action panels, which is one fact formatted two
 * ways on two screens. **Two:** $199.80 is not a projection at all. It is the ad set's **budget left**, a
 * fact about a budget the owner set, and it is already on screen to the cent where budgets are (DS-24,
 * REQ-076 AC2). Printing it inside an estimate dresses the estimate as an exact number.
 *
 * The "about" stays on the per-day rate and only there: it belongs to estimates of behaviour, not to
 * arithmetic on a value the owner has just typed (D-195's second half).
 */
function signed(value: Money, fmt: ReturnType<typeof createFormatter>): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${fmt(Math.abs(value), 'money.headline')}`;
}
